import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../config';

export default function OnboardingAdmin({ adminToken }) {
  const [trainings, setTrainings] = useState([]);
  const [showcase, setShowcase] = useState(null);
  const [selectedTrainingId, setSelectedTrainingId] = useState('');
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [loading, setLoading] = useState(true);
  const [showcaseLoading, setShowcaseLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [previewVideoUrl, setPreviewVideoUrl] = useState('');

  const getAuthHeaders = (withJson = false) => ({
    Authorization: `Bearer ${adminToken}`,
    ...(withJson ? { 'Content-Type': 'application/json' } : {})
  });

  const loadSettings = async () => {
    const res = await fetch(`${API_URL}/api/admin/onboarding/settings`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) {
      throw new Error('Onboarding-Settings konnten nicht geladen werden.');
    }

    const data = await res.json();
    const settings = data.settings || {};
    setSelectedTrainingId(settings.demo_avatar_training_id || '');
    setSelectedImageUrl(settings.selected_demo_image_url || '');
    setSelectedVideoId(settings.selected_demo_video_id || '');
    setShowcase(settings.demo_showcase || null);
  };

  const loadTrainings = async () => {
    const res = await fetch(`${API_URL}/api/admin/onboarding/trainings-picker`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) {
      throw new Error('Demo-Avatare konnten nicht geladen werden.');
    }

    const data = await res.json();
    setTrainings(data.trainings || []);
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        await Promise.all([loadTrainings(), loadSettings()]);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Fehler beim Laden der Onboarding-Einstellungen.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const previewTraining = async (trainingId) => {
    if (!trainingId) {
      setShowcase(null);
      setSelectedImageUrl('');
      setSelectedVideoId('');
      return;
    }

    try {
      setShowcaseLoading(true);
      setError('');
      const res = await fetch(`${API_URL}/api/admin/onboarding/preview/${encodeURIComponent(trainingId)}`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        throw new Error('Vorschau für diesen Avatar konnte nicht geladen werden.');
      }

      const data = await res.json();
      setShowcase(data.demo_showcase || null);
      setSelectedImageUrl(data.selected_demo_image_url || '');
      setSelectedVideoId(data.selected_demo_video_id || '');
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Vorschau.');
    } finally {
      setShowcaseLoading(false);
    }
  };

  const handleTrainingChange = async (event) => {
    const nextTrainingId = event.target.value;
    setSelectedTrainingId(nextTrainingId);
    setMessage('');
    await previewTraining(nextTrainingId);
  };

  const handleSave = async () => {
    if (!selectedTrainingId) {
      setError('Bitte zuerst einen Demo-Avatar auswählen.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setMessage('');

      const res = await fetch(`${API_URL}/api/admin/onboarding/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          demo_avatar_training_id: selectedTrainingId,
          selected_demo_image_url: selectedImageUrl || null,
          selected_demo_video_id: selectedVideoId || null
        })
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.detail || 'Speichern fehlgeschlagen.');
      }

      await loadSettings();
      setMessage('Onboarding-Demo gespeichert.');
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  const imageOptions = useMemo(() => {
    const allImages = (showcase?.generations || []).flatMap((generation) => generation.image_urls || []);
    return Array.from(new Set(allImages.filter(Boolean)));
  }, [showcase]);

  const videoOptions = useMemo(() => {
    return (showcase?.videos || []).filter((video) => video.video_url);
  }, [showcase]);

  const selectedTraining = trainings.find((training) => training.training_id === selectedTrainingId);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Onboarding Demo</h2>
          <p className="text-sm text-white/60 mt-1">
            Wähle genau den Avatar, das Bild und das Video aus, die in der geführten Tour verwendet werden.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {message && <span className="text-sm text-emerald-400">{message}</span>}
          {error && <span className="text-sm text-red-400">{error}</span>}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !selectedTrainingId}
            className="px-5 py-3 rounded-xl bg-[#10B981] text-black font-bold hover:bg-[#22c58b] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {saving ? 'Speichert...' : 'Speichern'}
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 md:p-6 space-y-5">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,320px),1fr]">
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-[#10B981]">
              Demo-Avatar
            </label>
            <select
              value={selectedTrainingId}
              onChange={handleTrainingChange}
              className="w-full rounded-2xl border border-white/15 bg-black/50 px-4 py-3 text-white outline-none focus:border-[#10B981]/60"
            >
              <option value="">Avatar auswählen</option>
              {trainings.map((training) => (
                <option key={training.training_id} value={training.training_id}>
                  {training.title}
                </option>
              ))}
            </select>
            <p className="text-xs text-white/45">
              Standard ist aktuell <span className="text-white/70 font-semibold">Mia</span>.
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-4">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#10B981] mb-2">
              Aktive Auswahl
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
              <span>Avatar: <span className="text-white">{selectedTraining?.title || showcase?.avatar?.title || 'Nicht gesetzt'}</span></span>
              <span>Bilder: <span className="text-white">{imageOptions.length}</span></span>
              <span>Videos: <span className="text-white">{videoOptions.length}</span></span>
            </div>
          </div>
        </div>

        {showcaseLoading && (
          <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-4 text-sm text-white/60">
            Lade Medien dieses Avatars...
          </div>
        )}

        {!showcaseLoading && !showcase && (
          <div className="rounded-2xl border border-dashed border-white/10 bg-black/30 px-4 py-10 text-center text-sm text-white/45">
            Für diesen Avatar sind noch keine Demo-Medien verfügbar.
          </div>
        )}

        {!showcaseLoading && showcase && (
          <div className="space-y-6">
            <section className="space-y-3">
              <div>
                <h3 className="text-lg font-semibold text-white">Demo-Bild</h3>
                <p className="text-sm text-white/55">
                  Dieses Bild wird in der Tour als erzeugtes Ergebnis und als Startpunkt für das Video verwendet.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {imageOptions.map((imageUrl, index) => {
                  const isSelected = selectedImageUrl === imageUrl;
                  return (
                    <button
                      key={imageUrl}
                      type="button"
                      onClick={() => setSelectedImageUrl(imageUrl)}
                      className={`group overflow-hidden rounded-3xl border text-left transition-all ${
                        isSelected
                          ? 'border-[#10B981] bg-[#10B981]/10 shadow-[0_0_0_1px_rgba(16,185,129,0.3)]'
                          : 'border-white/10 bg-black/30 hover:border-white/25'
                      }`}
                    >
                      <img
                        src={imageUrl}
                        alt={`Demo-Bild ${index + 1}`}
                        className="h-56 w-full object-cover"
                      />
                      <div className="px-4 py-3">
                        <p className="text-sm font-semibold text-white">
                          Bild {index + 1}
                        </p>
                        <p className="text-xs text-white/45 mt-1">
                          {isSelected ? 'Aktiv für die Onboarding-Tour' : 'Als Demo-Bild auswählen'}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="space-y-3">
              <div>
                <h3 className="text-lg font-semibold text-white">Demo-Video</h3>
                <p className="text-sm text-white/55">
                  Dieses Video wird am Ende der Tour abgespielt und im Video-Flow als fertiges Ergebnis gezeigt.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {videoOptions.map((video, index) => {
                  const isSelected = selectedVideoId === video.id;
                  const previewUrl = video.poster_thumbnail_url || video.source_image_url || selectedImageUrl || '';
                  return (
                    <div
                      key={video.id}
                      className={`group relative overflow-hidden rounded-3xl border text-left transition-all ${
                        isSelected
                          ? 'border-[#10B981] bg-[#10B981]/10 shadow-[0_0_0_1px_rgba(16,185,129,0.3)]'
                          : 'border-white/10 bg-black/30 hover:border-white/25'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedVideoId(video.id)}
                        className="block w-full text-left"
                      >
                        {previewUrl ? (
                          <img
                            src={previewUrl}
                            alt={`Demo-Video ${index + 1}`}
                            className="h-56 w-full object-cover"
                          />
                        ) : (
                          <div className="h-56 w-full bg-white/5 flex items-center justify-center text-white/35 text-sm">
                            Keine Vorschau
                          </div>
                        )}
                        <div className="px-4 py-3">
                          <p className="text-sm font-semibold text-white">
                            Video {index + 1}
                          </p>
                          <p className="text-xs text-white/45 mt-1">
                            {isSelected ? 'Aktiv für die Onboarding-Tour' : 'Als Demo-Video auswählen'}
                          </p>
                        </div>
                      </button>
                      {video.video_url && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewVideoUrl(video.video_url);
                          }}
                          aria-label="Video abspielen"
                          title="Video abspielen"
                          className="absolute top-3 right-3 w-11 h-11 rounded-full bg-black/70 hover:bg-black/85 border border-white/25 text-white flex items-center justify-center backdrop-blur-sm transition-all"
                        >
                          <svg className="w-5 h-5 ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        )}
      </div>
      {previewVideoUrl && (
        <div
          className="fixed inset-0 z-[100000] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreviewVideoUrl('')}
        >
          <button
            type="button"
            onClick={() => setPreviewVideoUrl('')}
            aria-label="Preview schließen"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-black/70 border border-white/20 text-white text-2xl leading-none flex items-center justify-center"
          >
            ×
          </button>
          <video
            key={previewVideoUrl}
            src={previewVideoUrl}
            autoPlay
            controls
            playsInline
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[90vh] rounded-2xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
